import { useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import type {
  BulkApprovalCase,
  BulkSelectionSummary,
} from '~/pages/SpotBidding/models/bulkApproval'
import {
  collectUnverifiedSections,
  countApproverNotices,
  needsReconfirm,
  pickSelectedCases,
  summarizeBulkSelection,
} from '~/pages/SpotBidding/models/bulkApproval'

export type BulkSelection = {
  /** 選択から外した案件の id (既定は空 = 全件選択)。 */
  deselectedIds: ReadonlySet<string>
  toggle: (id: string, checked: boolean) => void
  allSelected: boolean
  toggleAll: (checked: boolean) => void
  selected: BulkApprovalCase[]
  summary: BulkSelectionSummary
  /** 承認者非該当・自己承認の案件数 (選択中)。 */
  notApproverCount: number
  selfApprovalCount: number
  /** 再確認 (AC-BSP-098 (b)) を 1 回経る必要があるか。 */
  needsReconfirm: boolean
  reconfirmed: boolean
  setReconfirmed: (checked: boolean) => void
  /** 選択中の案件の、 入札制限を判定できていない理由 (見出しで重複を除く)。 */
  unverifiedSections: ViolationSection[]
  /** 確定できない理由 (undefined なら確定できる)。 */
  confirmBlockedReason: string | undefined
}

/**
 * 確認モーダルの案件の選択と、 選択に追随する合計・再確認の要否。
 *
 * 確認モーダルのコンポーネントの中で使う (= 選択の切替でページ全体を描き直さない)。
 * 既定は全件選択で、 外した案件だけを覚えるので、 開いている間に増えた案件も選択に入る。
 */
export const useBulkSelection = (
  cases: readonly BulkApprovalCase[],
): BulkSelection => {
  const { t } = useTranslation()
  const [deselectedIds, setDeselectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const [reconfirmedKey, setReconfirmedKey] = useState<string | undefined>(
    undefined,
  )

  const selected = useMemo(
    () => pickSelectedCases(cases, deselectedIds),
    [cases, deselectedIds],
  )
  const summary = useMemo(() => summarizeBulkSelection(selected), [selected])
  const unverifiedSections = useMemo(
    () => collectUnverifiedSections(selected),
    [selected],
  )
  const notices = useMemo(() => countApproverNotices(selected), [selected])
  const reconfirmRequired = needsReconfirm(notices)
  // 再確認は「この件数の注意」 に対するもの。 選択を変えて件数が変わったら取り直す。
  const reconfirmKey = `${String(notices.notApprover)}:${String(notices.selfApproval)}`
  const reconfirmed = reconfirmedKey === reconfirmKey

  let confirmBlockedReason: string | undefined
  if (selected.length === 0) {
    confirmBlockedReason = t(
      'page:biddingCommon.bulkApprove.blocked.noSelection',
    )
  } else if (unverifiedSections.length > 0) {
    // 入札制限を判定できていない案件は送らない (単件の承認確認と同じ fail-closed)。
    // 「違反」 の案件は送れる (承認可否は backend) のと分ける。
    confirmBlockedReason = unverifiedSections[0]?.title
  } else if (reconfirmRequired && !reconfirmed) {
    confirmBlockedReason = t('page:biddingCommon.bulkApprove.blocked.reconfirm')
  }

  // 数千件の行を再描画しないよう、 行に渡す操作は参照を安定させる。
  const toggle = useCallback((id: string, checked: boolean): void => {
    setDeselectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])
  const toggleAll = useCallback(
    (checked: boolean): void => {
      setDeselectedIds(checked ? new Set() : new Set(cases.map((c) => c.id)))
    },
    [cases],
  )

  return {
    deselectedIds,
    toggle,
    allSelected: cases.length > 0 && selected.length === cases.length,
    toggleAll,
    selected,
    summary,
    notApproverCount: notices.notApprover,
    selfApprovalCount: notices.selfApproval,
    needsReconfirm: reconfirmRequired,
    reconfirmed,
    setReconfirmed: (checked) => {
      setReconfirmedKey(checked ? reconfirmKey : undefined)
    },
    unverifiedSections,
    confirmBlockedReason,
  }
}
